'use client';

// The sidebar's footer: the signed-in staff member's chip and its My Profile /
// Log Out menu. Split out of Sidebar.tsx (BL-2422). In the phone app, Log Out is
// disabled while offline (BUG-2071, operator decision, Michael Lawrie 2026-10-13),
// and so is My Profile, whose reads and save need the server (BUG-2077).

import { useEffect, useRef, useState } from 'react';
import { ChevronUp, LogOut, User, type LucideIcon } from 'lucide-react';
import { clsx } from 'clsx';
import { Spinner } from '@auctogon/ui/web';
import { useAuth } from '../auth/AuthProvider';
import { staffInitials } from '@auctogon/core-admin/services/staff-names';
import Avatar from '../ui/Avatar';
import Alert from '../ui/Alert';
import { useOfflineMode } from '../offline/OfflineModeProvider';
import { NEEDS_CONNECTION } from '../offline/offline-copy';
import NeedsConnectionMark from '../offline/NeedsConnectionMark';

/**
 * BUG-2071 backstop: shown when Log Out was pressed online but the server never confirmed the
 * sign-out (the connection dropped before the app noticed), so nothing was signed out.
 */
const SIGN_OUT_FAILED =
  "Couldn't sign out. Try again.";

/**
 * A menu row that needs a connection the app doesn't have: visible but disabled with the no-wifi
 * mark, as the sidebar's other connection-only entries are (BL-2422). It has no click handler.
 *
 * `aria-disabled`, not native `disabled`, so it stays reachable with its reason, as
 * `SidebarNav`'s offline entries do. Its muted label is `--sidebar-text` at 60% on the menu's
 * `--sidebar-active` fill:
 *   #849490 on #0d3633 ....... 4.16:1  text (needs 4.5) — exempt, disabled
 * WCAG 2.2 1.4.3 exempts a disabled control. The second signal (ADR 051 — Contrast Floor
 * rule 3) is the no-wifi mark plus `aria-disabled`.
 *
 * @param props.icon - the row's glyph.
 * @param props.label - the row's label.
 * @param props.testId - its automation handle.
 * @returns The disabled row.
 */
function NeedsConnectionRow({ icon: Icon, label, testId }: { icon: LucideIcon; label: string; testId: string }) {
  return (
    // eslint-disable-next-line designsystem/no-raw-button -- non-CTA menu row on the dark nav rail (offline, needs connection)
    <button
      type="button"
      aria-disabled="true"
      data-state="needs-connection"
      data-testid={testId}
      title={NEEDS_CONNECTION}
      className="w-full flex items-center px-4 py-3 text-body text-sidebar-text/60 cursor-default"
    >
      <Icon className="w-4 h-4 mr-3" aria-hidden />
      <span className="flex-1 text-left">{label}</span>
      <NeedsConnectionMark />
    </button>
  );
}

/**
 * My Profile. Offline it is a {@link NeedsConnectionRow}: the Profile modal reads and saves
 * through profile routes the offline transport doesn't answer (BUG-2077).
 *
 * @param props.onOpenProfile - opens the Profile modal, only while online.
 * @returns The My Profile row.
 */
function MyProfileButton({ onOpenProfile }: { onOpenProfile: () => void }) {
  const { isOffline } = useOfflineMode();
  if (isOffline) return <NeedsConnectionRow icon={User} label="My profile" testId="sidebar-my-profile" />;
  return (
    // eslint-disable-next-line designsystem/no-raw-button -- non-CTA menu row on the dark nav rail
    <button
      type="button"
      onClick={onOpenProfile}
      data-testid="sidebar-my-profile"
      className="w-full flex items-center px-4 py-3 text-body text-sidebar-text hover:bg-sidebar-border transition-colors"
    >
      <User className="w-4 h-4 mr-3 text-sidebar-text/70" />
      My profile
    </button>
  );
}

/**
 * Log Out. Offline it is a {@link NeedsConnectionRow}: signing out revokes the server session,
 * and with no connection that request never arrives, so the session cookie would outlive a
 * local sign-out (BUG-2071).
 *
 * @param props.onLogout - the sign-out, run only while online.
 * @param props.signingOut - a sign-out is in flight: the row shows a spinner and ignores presses.
 * @returns The Log Out row.
 */
function LogOutButton({ onLogout, signingOut }: { onLogout: () => void; signingOut: boolean }) {
  const { isOffline } = useOfflineMode();
  if (isOffline) return <NeedsConnectionRow icon={LogOut} label="Log out" testId="sidebar-log-out" />;
  return (
    // eslint-disable-next-line designsystem/no-raw-button -- non-CTA menu row on the dark nav rail
    <button
      type="button"
      onClick={signingOut ? undefined : onLogout}
      aria-disabled={signingOut ? 'true' : undefined}
      aria-busy={signingOut ? 'true' : undefined}
      data-testid="sidebar-log-out"
      className="w-full flex items-center px-4 py-3 text-body text-red-400 hover:bg-sidebar-border transition-colors"
    >
      {signingOut ? <Spinner size={16} className="mr-3" /> : <LogOut className="w-4 h-4 mr-3" />}
      Log out
    </button>
  );
}

/** Props for {@link UserMenuPanel}. */
interface UserMenuPanelProps {
  isCollapsed: boolean;
  /** The last Log Out was refused: the server never confirmed it (BUG-2071). */
  signOutRefused: boolean;
  /** A Log Out is waiting on the server's revoke. */
  signingOut: boolean;
  onOpenProfile: () => void;
  onLogout: () => void;
}

/**
 * The open menu: My Profile, and Log Out with its refusal message.
 *
 * @param props - see {@link UserMenuPanelProps}
 * @returns The menu panel.
 */
function UserMenuPanel({ isCollapsed, signOutRefused, signingOut, onOpenProfile, onLogout }: UserMenuPanelProps) {
  return (
    <div
      className={clsx(
        'absolute bg-sidebar-active border border-sidebar-border rounded-lg shadow-xl overflow-hidden z-20',
        isCollapsed ? 'bottom-2 left-full ml-2 w-56' : 'bottom-full left-3 right-3 mb-2'
      )}
    >
      <MyProfileButton onOpenProfile={onOpenProfile} />
      <div className="border-t border-sidebar-border" />
      {signOutRefused && (
        <div className="p-2" data-testid="sidebar-sign-out-error">
          <Alert kind="danger" variant="classic">{SIGN_OUT_FAILED}</Alert>
        </div>
      )}
      <LogOutButton onLogout={onLogout} signingOut={signingOut} />
    </div>
  );
}

/** Props for {@link SidebarUserMenu}. */
export interface SidebarUserMenuProps {
  isCollapsed: boolean;
  onOpenProfile: () => void;
}

/**
 * The staff chip and its menu.
 *
 * @param props - see {@link SidebarUserMenuProps}
 * @returns The footer block.
 */
export default function SidebarUserMenu({ isCollapsed, onOpenProfile }: SidebarUserMenuProps) {
  const { signOut, user } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [signOutRefused, setSignOutRefused] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  // Close the dropdown when the sidebar toggles so it doesn't linger in a stale
  // position. Derived state avoids a cascading re-render.
  const [prevCollapsed, setPrevCollapsed] = useState(isCollapsed);
  if (isCollapsed !== prevCollapsed) {
    setPrevCollapsed(isCollapsed);
    setIsDropdownOpen(false);
  }
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setSignOutRefused(false);
    setSigningOut(true);
    const outcome = await signOut();
    // BUG-2071: an unconfirmed revoke signed nothing out, so stay put and say why.
    if (!outcome.success) {
      setSigningOut(false);
      setSignOutRefused(true);
    }
    // BUG-2233: on success the sign-out has already started the one load of sign-in, and the row
    // keeps its spinner until the page goes. A second navigation here would cancel it.
  };

  const userLabel = user?.name || user?.email || 'User';

  return (
    <div
      className={clsx(
        'border-t border-sidebar-border relative',
        isCollapsed ? 'p-2' : 'p-4'
      )}
      ref={dropdownRef}
    >
      {isDropdownOpen && (
        <UserMenuPanel
          isCollapsed={isCollapsed}
          signOutRefused={signOutRefused}
          signingOut={signingOut}
          onOpenProfile={() => {
            onOpenProfile();
            setIsDropdownOpen(false);
          }}
          onLogout={handleLogout}
        />
      )}

      {/* eslint-disable-next-line designsystem/no-raw-button -- non-CTA menu trigger on the dark nav rail: opens the user menu */}
      <button
        type="button"
        onClick={() => {
          setIsDropdownOpen(!isDropdownOpen);
          // A reopened menu starts clean; a refusal belongs to the attempt it reported.
          setSignOutRefused(false);
        }}
        title={isCollapsed ? userLabel : undefined}
        aria-label={isCollapsed ? userLabel : undefined}
        className={clsx(
          // BL-1411: `min-h-11` (44px) because below 1024px this collapsed rail
          // is the ONLY sign-out affordance on a phone, and its 8px avatar in a
          // `p-1` box measured 40px — under the coarse-pointer target floor.
          'w-full flex items-center hover:bg-sidebar-border rounded-lg transition-colors min-h-11',
          isCollapsed ? 'justify-center p-1' : 'p-2 -m-2'
        )}
      >
        {/* The string overload routes through the shared splitter, so the
            avatar can never disagree with the label beside it. `accent` is the
            bg-secondary pairing the chip always had, measured 4.92:1 (ADR 051 —
            Contrast Floor); BL-2145 retired the hand-rolled circle. */}
        <Avatar initials={staffInitials(user?.name)} size="md" tone="accent" />
        {!isCollapsed && (
          <>
            <div className="ml-3 flex-1 text-left">
              <p className="text-body font-medium text-sidebar-text truncate">
                {/* BUG-1650: `user.name` is the one resolved staff display name
                    (parts → email → sentinel), so it is never blank. The
                    `email.split('@')[0] || 'User'` chain that used to be here
                    was unreachable anyway — the session mapper had already
                    substituted the full email — and it was one of four
                    different answers for a nameless operator. */}
                {user?.name}
              </p>
            </div>
            <ChevronUp
              className={clsx(
                'w-4 h-4 text-sidebar-text/60 transition-transform',
                isDropdownOpen ? 'rotate-180' : ''
              )}
            />
          </>
        )}
      </button>
    </div>
  );
}
